'use client';

import Icon from '@/components/ui/AppIcon';

interface DroneTechCollaborator {
  name: string;
  focus: string;
  description: string;
  capabilities: string[];
  icon: string;
  website?: string;
}

const DroneTechCollaboratorsSection = () => {
  const droneTechCollaborators: DroneTechCollaborator[] = [
    {
      name: 'Drone Tech Partner',
      focus: 'Aerial Monitoring & Environmental Assessment',
      description: 'Advanced drone technology partner specializing in environmental monitoring, carbon stock assessment, and ecosystem mapping using cutting-edge aerial imaging and AI-powered analytics.',
      capabilities: [
        'Carbon Stock Measurement',
        'Forest Canopy Analysis',
        'Land Use Mapping',
        'Precision Agriculture Monitoring',
        'Environmental Impact Assessment',
        'Real-time Data Collection',
      ],
      icon: 'CameraIcon',
      website: '',
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-surface to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-4">
            Drone Tech <span className="text-primary">Collaborators</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Leveraging advanced aerial technology for precise environmental monitoring and carbon assessment
          </p>
        </div>

        {/* Drone Tech Collaborators */}
        <div className="max-w-5xl mx-auto">
          {droneTechCollaborators.map((collaborator, index) => (
            <div
              key={index}
              className="bg-card border-2 border-primary/20 rounded-2xl p-8 hover:shadow-natural-xl hover:border-primary/40 transition-all duration-500 ease-organic group"
            >
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Left Column - Icon & Overview */}
                <div className="lg:col-span-1">
                  <div className="w-20 h-20 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                    <Icon 
                      name={collaborator.icon} 
                      size={40} 
                      className="text-primary group-hover:text-white transition-colors" 
                    />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-text-primary mb-2 group-hover:text-primary transition-colors">
                    {collaborator.name}
                  </h3>
                  <p className="font-semibold text-primary mb-4">
                    {collaborator.focus}
                  </p>
                  {collaborator.website && (
                    <a
                      href={collaborator.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-sm text-primary hover:underline"
                    >
                      <Icon name="GlobeAltIcon" size={16} />
                      <span>Visit Website</span>
                      <Icon name="ArrowTopRightOnSquareIcon" size={14} />
                    </a>
                  )}
                </div>

                {/* Middle Column - Description */}
                <div className="lg:col-span-1">
                  <h4 className="font-heading font-bold text-text-primary mb-3 flex items-center space-x-2">
                    <Icon name="InformationCircleIcon" size={20} className="text-primary" />
                    <span>About</span>
                  </h4>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {collaborator.description}
                  </p>
                </div>

                {/* Right Column - Capabilities */}
                <div className="lg:col-span-1">
                  <h4 className="font-heading font-bold text-text-primary mb-3 flex items-center space-x-2">
                    <Icon name="CircleStackIcon" size={20} className="text-primary" />
                    <span>Key Capabilities</span>
                  </h4>
                  <ul className="space-y-2">
                    {collaborator.capabilities.map((capability, idx) => (
                      <li
                        key={idx}
                        className="flex items-start space-x-2 text-sm text-text-secondary"
                      >
                        <Icon
                          name="CheckCircleIcon"
                          size={18}
                          className="text-primary flex-shrink-0 mt-0.5"
                        />
                        <span>{capability}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 bg-primary/5 border border-primary/20 rounded-xl">
            <Icon name="SparklesIcon" size={32} className="text-primary" />
            <div className="text-left">
              <p className="font-heading font-bold text-text-primary mb-1">
                Interested in Drone Tech Collaboration?
              </p>
              <p className="text-sm text-text-secondary">
                Partner with us to leverage cutting-edge aerial technology for environmental impact
              </p>
            </div>
            <a
              href="mailto:contact@gencarbon.com"
              className="px-6 py-3 bg-primary text-primary-foreground font-heading font-semibold text-sm rounded-lg hover:bg-primary/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic whitespace-nowrap"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DroneTechCollaboratorsSection;